/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CalendarDto, CalendarListItemDto } from '@gh/builder-shared-data';
import { createGroupIndexDriver, createTableDriver, Table, whenExisting } from '@gh/common-shared';
import { currentNormalizedDate, valueComparator } from '@gh/core-util';

import { omit, property } from 'lodash';

export type Calendar = { dates: CalendarListItem[] };
export type CalendarListItem = CalendarListItemDto & { dateId: string; year: number };
export type CalendarDetails = {
  dates: Table<CalendarListItem>;
  preloadedYears: number[];
  selectedDate: Date;
  visibleDate: Date;
  currentDescription: string;
};

export const getCalendarListItemId = (item: CalendarListItem) => item.dateId;

export const calendarTable = createTableDriver(
  getCalendarListItemId,
  createGroupIndexDriver(
    'year',
    getCalendarListItemId,
    whenExisting,
    property('year'),
    valueComparator(property('date'))));

export const CALENDAR_DETAILS_INIT: CalendarDetails = {
  dates: calendarTable.create(),
  preloadedYears: [],
  selectedDate: currentNormalizedDate(),
  visibleDate: currentNormalizedDate(),
  currentDescription: '',
};

export const generateDateId = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

export const createNewCalendarListItem = (date: Date, description: string): CalendarListItem => ({
  dateId: generateDateId(date),
  date,
  year: date.getFullYear(),
  description,
});

export const calendarFromDto = (dto: CalendarDto): Calendar => ({
  dates: dto.dates.map((item) => ({
    ...item,
    dateId: generateDateId(item.date),
    year: item.date.getFullYear(),
  })),
});

export const calendarToDto = (dto: Calendar): CalendarDto => ({
  dates: dto.dates.map((item) => <CalendarListItem>omit(item, 'dateId', 'year')),
});
